// `reports/usage-report/*` — the filter bar, the nine-column table and the CSV button.
import { createElement, useMemo, useState, type ReactElement, type ReactNode } from 'react'
import type { UsageReportRequest, UsageReportResponse, UserUsageReport } from '@woo-ext/api'
import { nextSortOrder, SortIndicator, type SortOrder } from '../common/SortIndicator.tsx'
import { GenerateUserUsageReportForm } from './GenerateUserUsageReportForm.tsx'
import { InfoTooltip } from './InfoTooltip.tsx'
import {
  departmentCell,
  showsCsvButton,
  sortUsers,
  totalCountCell,
  totalRateCell,
} from './usageRows.ts'
import { countCell, rateCell } from './format.ts'

export interface UsageReportProps {
  report?: UsageReportResponse
  loading: boolean
  onSubmit: (request: UsageReportRequest) => void
  onExportCsv: () => void
  /**
   * `window.careero.atsName` — an identity: it decides only `isBullhorn`
   * (`ATSType.BULLHORN === careero.atsName`), the tooltip's tearsheet clause.
   */
  atsName?: string
  /**
   * `ctrl.atsLabel` — the name the tooltip SAYS (`shortlist-candidates-exported-tooltip-ats-name`,
   * relabelled by 5cfd3ef899). Falls back to `atsName` when the caller has no label.
   */
  atsLabel?: string
}

const MESSAGED_TOOLTIP = [
  'Candidates who have received at least one message through our platform.',
  "It's important to note that each candidate is counted only once, even if they receive multiple messages.",
  'If sending mailshots to multiple candidates, the number of messaged candidates may be higher than viewed candidates.',
]

const OPEN_RATE_TOOLTIP = [
  'Calculated as "number of opened messages" / "number of sent messages".',
  'This includes both original and follow-up messages.',
]

const RESPONSE_RATE_TOOLTIP = [
  'Calculated as "number of responded messages" / "number of sent messages".',
  'This includes both original and follow-up messages.',
]

/**
 * `shortlistCandidatesExportedTooltip` builds its text from the ATS's LABEL, and adds the
 * tearsheet clause on Bullhorn.
 *
 * ⚠️ `isBullhorn` IS `ATSType.BULLHORN === careero.atsName`, and `ATSType.BULLHORN` is
 * `'Bullhorn'`. This compared against `'BULLHORN'`, which no tenant's `atsName` is, so the clause
 * never appeared; and once the name in the sentence became a label it could not be the key anyway.
 */
export function exportedTooltip(atsLabel: string, isBullhorn: boolean): string[] {
  const bullhorn = isBullhorn ? ' or sent to a Bullhorn tearsheet/hotlist via Woo' : ''
  return [
    `Candidates who have been added to ${atsLabel} job through Woo${bullhorn}.`,
    'Please keep in mind that if exporting batches of multiple candidates, the number of exported candidates may be higher than viewed candidates.',
  ]
}

export function UsageReport({
  report,
  loading,
  onSubmit,
  onExportCsv,
  atsName = '',
  atsLabel = atsName,
}: UsageReportProps): ReactElement {
  const [order, setOrder] = useState<SortOrder>({ field: 'name', reverse: false })

  const users = useMemo(() => sortUsers(report?.users ?? [], order), [report, order])
  const total = report?.total
  const sort = (field: string): void => {
    setOrder((current) => nextSortOrder(current, field))
  }

  return createElement(
    'usage-report',
    null,
    <div className="usage-report">
      <div className="report-section-header">
        <h2>Usage</h2>
        {/* The `.report-period` h3 is commented out upstream; it is not ported. */}
      </div>
      <div className="report-section-body">
        <GenerateUserUsageReportForm onSubmit={onSubmit} />
        {loading && report === undefined ? (
          <div className="woo-loader-wrapper">
            <i className="far fa-spinner fa-spin woo-loader" />
          </div>
        ) : null}
        {!loading && report !== undefined ? (
          <table className="usage-report-table nicer-scroller mac-style" id="usage-report-table">
            <thead>
              {/* ⚠️ EIGHT EMPTY `th`s THEN THE BUTTON. The export row is a real table row, so
                  the button sits in the ninth column and the eight before it must exist or
                  it slides left under "Name". And the row only renders when there is data —
                  `users.length > 0 && total` — so an empty report has no CSV button. */}
              {showsCsvButton(users.length, total !== undefined) ? (
                <tr className="csv-export-button-containing-row">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <th key={i} />
                  ))}
                  <th>
                    <div className="th-content-wrapper" onClick={onExportCsv}>
                      <button className="btn btn-primary export-to-csv-button" type="button">
                        <i className="fas fa-file-csv cursor-pointer" title="Download usage report" />
                        Download
                      </button>
                    </div>
                  </th>
                </tr>
              ) : null}
              <tr>
                <HeaderCell title="Name" field="name" order={order} onSort={sort} />
                <HeaderCell title="Email" field="email" order={order} onSort={sort} />
                <HeaderCell title="Department" field="department" order={order} onSort={sort} />
                <HeaderCell title="Searches created" field="huntersCreated" order={order} onSort={sort} />
                <HeaderCell title="Candidates viewed" field="candidatesViewed" order={order} onSort={sort} />
                <HeaderCell
                  title="Candidates exported"
                  field="candidatesExported"
                  order={order}
                  onSort={sort}
                  info={<InfoTooltip title="Candidates exported" content={exportedTooltip(atsLabel, atsName === 'Bullhorn')} />}
                />
                <HeaderCell
                  title="Candidates messaged"
                  field="candidatesMessaged"
                  order={order}
                  onSort={sort}
                  info={<InfoTooltip title="Candidates messaged" content={MESSAGED_TOOLTIP} />}
                />
                {/* The inline widths and `word-break` are upstream's, on the element. */}
                <HeaderCell
                  title="Open %"
                  field="candidatesMessageOpenedRate"
                  order={order}
                  onSort={sort}
                  thStyle={{ minWidth: '115px' }}
                  titleStyle={{ wordBreak: 'keep-all' }}
                  info={<InfoTooltip title="Open %" content={OPEN_RATE_TOOLTIP} placement="top-right" />}
                />
                <HeaderCell
                  title="Response %"
                  field="candidatesMessageRespondedRate"
                  order={order}
                  onSort={sort}
                  thStyle={{ minWidth: '145px' }}
                  titleStyle={{ wordBreak: 'keep-all' }}
                  info={<InfoTooltip title="Response %" content={RESPONSE_RATE_TOOLTIP} placement="top-right" />}
                />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.email}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  {/* An empty department reads "No department", not blank. */}
                  <td>{departmentCell(user.department)}</td>
                  <td>{countCell(user.huntersCreated)}</td>
                  <td>{countCell(user.candidatesViewed)}</td>
                  <td>{countCell(user.candidatesExported)}</td>
                  <td>{countCell(user.candidatesMessaged)}</td>
                  <td>{rateCell(user.candidatesMessageOpenedRate)}</td>
                  <td>{rateCell(user.candidatesMessageRespondedRate)}</td>
                </tr>
              ))}
              {total !== undefined ? (
                <tr className="total-row">
                  <td>{total.name}</td>
                  <td>{total.email}</td>
                  <td>{total.department}</td>
                  {/* ⚠️ THE TOTALS ARE GATED ON `users.length > 0` AS WELL AS ON isNaN. With no
                      rows the server still sends a total object, and upstream renders N/A
                      rather than a column of zeroes under an empty table. */}
                  <td>{totalCountCell(users.length, total.huntersCreated)}</td>
                  <td>{totalCountCell(users.length, total.candidatesViewed)}</td>
                  <td>{totalCountCell(users.length, total.candidatesExported)}</td>
                  <td>{totalCountCell(users.length, total.candidatesMessaged)}</td>
                  <td>{totalRateCell(users.length, total.candidatesMessageOpenedRate)}</td>
                  <td>{totalRateCell(users.length, total.candidatesMessageRespondedRate)}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        ) : null}
      </div>
    </div>,
  )
}

function HeaderCell({
  title,
  field,
  order,
  onSort,
  info,
  thStyle,
  titleStyle,
}: {
  title: string
  field: string
  order: SortOrder
  onSort: (field: string) => void
  info?: ReactNode
  thStyle?: Record<string, string>
  titleStyle?: Record<string, string>
}): ReactElement {
  return (
    <th {...(thStyle === undefined ? {} : { style: thStyle })}>
      <div className="th-content-wrapper">
        <div className="header-title" {...(titleStyle === undefined ? {} : { style: titleStyle })}>
          {title}
        </div>
        <div className="header-actions">
          {info}
          <SortIndicator field={field} order={order} onSort={onSort} />
        </div>
      </div>
    </th>
  )
}
